import React, { useState } from "react";
import {
  Box,
  Button,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  IconButton,
  Typography,
} from "@mui/material";
import {
  CloudUpload,
  AttachFile,
  Delete,
} from "@mui/icons-material";

/**
 * Reusable FormFileUpload component with file preview
 * 
 * @param {string} name - Field name
 * @param {Function} onChange - Change handler: (files) => void
 * @param {Array} value - Current files array
 * @param {boolean} multiple - Allow multiple files
 * @param {string} accept - Accepted file types (e.g., ".pdf,.doc,.docx")
 * @param {number} maxSize - Max file size in MB
 * @param {string} label - Upload button label
 * @param {object} sx - Custom styles
 */
const FormFileUpload = ({
  name,
  onChange,
  value = [],
  multiple = false,
  accept,
  maxSize,
  label = "Upload Files",
  sx = {},
}) => {
  const [error, setError] = useState("");

  const handleFileChange = (event) => {
    const files = Array.from(event.target.files);
    setError("");

    // Validate file sizes
    if (maxSize) {
      const oversizedFiles = files.filter(
        (file) => file.size > maxSize * 1024 * 1024
      );
      if (oversizedFiles.length > 0) {
        setError(
          `Some files exceed the maximum size of ${maxSize}MB`
        );
        return;
      }
    }

    const newFiles = multiple ? [...value, ...files] : files;
    onChange?.(newFiles);
    event.target.value = ""; // Reset input
  };

  const handleRemoveFile = (index) => {
    const newFiles = value.filter((_, i) => i !== index);
    onChange?.(newFiles);
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + " " + sizes[i];
  };

  const getSafeFileDisplayName = (file, index) => {
    const name = file?.name;
    if (typeof name !== "string") return `File ${index + 1}`;
    if (name.startsWith("http") || name.includes("/") || name.length > 80) {
      return `File ${index + 1}`;
    }
    return name;
  };

  return (
    <Box sx={sx}>
      <input
        type="file"
        multiple={multiple}
        accept={accept}
        onChange={handleFileChange}
        style={{ display: "none" }}
        id={`file-upload-${name}`}
      />
      <label htmlFor={`file-upload-${name}`}>
        <Button
          variant="outlined"
          component="span"
          startIcon={<CloudUpload />}
          fullWidth
        >
          {label}
        </Button>
      </label>

      {error && (
        <Typography variant="caption" color="error" sx={{ mt: 1, display: "block" }}>
          {error}
        </Typography>
      )}

      {value.length > 0 && (
        <List dense sx={{ mt: 2 }}>
          {value.map((file, index) => (
            <ListItem
              key={index}
              secondaryAction={
                <IconButton
                  edge="end"
                  size="small"
                  onClick={() => handleRemoveFile(index)}
                >
                  <Delete />
                </IconButton>
              }
            >
              <ListItemIcon>
                <AttachFile />
              </ListItemIcon>
              <ListItemText
                primary={getSafeFileDisplayName(file, index)}
                secondary={file.size != null ? formatFileSize(file.size) : ""}
              />
            </ListItem>
          ))}
        </List>
      )}
    </Box>
  );
};

export default FormFileUpload;

